<template>
  <section class="flex flex-col items-center justify-center min-h-[60vh]">
    <div class="flex flex-col py-8 gap-2 justify-center items-center">
      <h1 class="text-4xl text-base-content font-bold">Soleando</h1>
      <p>Encuentra tu próximo gran viaje</p>
    </div>

    <!-- Show context message if saving a trip -->
    <div v-if="actionMessage" class="alert alert-info max-w-md mb-6">
      <Icon name="heroicons:information-circle" />
      <span>{{ actionMessage }}</span>
    </div>

    <form
      class="w-full max-w-sm bg-base-100 p-8 rounded-lg shadow-lg space-y-6"
      @submit.prevent="onSubmit"
    >
      <!-- Mode Toggle -->
      <div class="tabs tabs-boxed justify-center">
        <button
          type="button"
          class="tab"
          :class="{ 'tab-active': mode === 'login' }"
          @click="mode = 'login'"
        >
          Iniciar sesión
        </button>
        <button
          type="button"
          class="tab"
          :class="{ 'tab-active': mode === 'signup' }"
          @click="mode = 'signup'"
        >
          Registrarse
        </button>
      </div>

      <h2 class="text-2xl font-bold mb-6 text-center">
        {{ mode === "login" ? "Iniciar sesión" : "Crear cuenta" }}
      </h2>

      <!-- Name field for signup -->
      <div v-if="mode === 'signup'" class="form-control w-full">
        <label class="label" for="name">
          <span class="label-text">Nombre completo</span>
        </label>
        <input
          id="name"
          v-model="name"
          type="text"
          class="input input-bordered w-full"
          placeholder="Ingresa tu nombre"
          required
        />
      </div>

      <div class="form-control w-full">
        <label class="label" for="email">
          <span class="label-text">Correo electrónico</span>
        </label>
        <input
          id="email"
          v-model="email"
          type="email"
          class="input input-bordered w-full"
          placeholder="Ingresa tu correo"
          required
        />
      </div>
      <div class="form-control w-full">
        <label class="label" for="password">
          <span class="label-text">Contraseña</span>
        </label>
        <input
          id="password"
          v-model="password"
          type="password"
          class="input input-bordered w-full"
          :placeholder="
            mode === 'signup' ? 'Mínimo 6 caracteres' : 'Ingresa tu contraseña'
          "
          :minlength="mode === 'signup' ? 6 : undefined"
          required
        />
      </div>

      <!-- Confirm password for signup -->
      <div v-if="mode === 'signup'" class="form-control w-full">
        <label class="label" for="confirmPassword">
          <span class="label-text">Confirmar contraseña</span>
        </label>
        <input
          id="confirmPassword"
          v-model="confirmPassword"
          type="password"
          class="input input-bordered w-full"
          placeholder="Confirma tu contraseña"
          required
        />
      </div>

      <button type="submit" class="btn btn-primary w-full" :disabled="loading">
        <span v-if="loading" class="loading loading-spinner loading-xs"></span>
        <span v-else>
          {{ mode === "login" ? "Entrar" : "Crear cuenta" }}
        </span>
      </button>

      <p v-if="error" class="text-error text-center mt-2">{{ error }}</p>
      <p v-if="success" class="text-success text-center mt-2">{{ success }}</p>
    </form>
  </section>
</template>

<script setup>
const route = useRoute();
const router = useRouter();
const supabase = useSupabaseClient();

const mode = ref("login");
const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const name = ref("");
const error = ref("");
const success = ref("");
const loading = ref(false);

// Get redirect URL and action from query params
const redirectUrl = route.query.redirect || "/";
const action = route.query.action;

// Show appropriate message based on action
const actionMessage = computed(() => {
  if (action === "save-trip") {
    return "Para guardar tu viaje, inicia sesión o crea una cuenta gratuita";
  } else if (action === "book-trip") {
    return "Para reservar tu viaje, inicia sesión o crea una cuenta gratuita";
  } else if (action === "save-customized-trip") {
    return "Para guardar tu viaje personalizado, inicia sesión o crea una cuenta gratuita";
  }
  return null;
});

const onSubmit = async () => {
  error.value = "";
  success.value = "";
  loading.value = true;

  try {
    if (mode.value === "login") {
      await handleLogin();
    } else {
      await handleSignup();
    }
  } catch (err) {
    console.error("Authentication error:", err);
    error.value =
      "Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.";
  } finally {
    loading.value = false;
  }
};

async function handleLogin() {
  const { error: loginError } = await supabase.auth.signInWithPassword({
    email: email.value,
    password: password.value,
  });

  if (loginError) {
    if (loginError.message.includes("Invalid login credentials")) {
      error.value = "Correo o contraseña incorrectos.";
    } else {
      error.value = loginError.message;
    }
  } else {
    // Successful login - handle different redirect scenarios
    await handleSuccessfulAuth();
  }
}

async function handleSignup() {
  // Validation
  if (password.value !== confirmPassword.value) {
    error.value = "Las contraseñas no coinciden.";
    return;
  }

  if (password.value.length < 6) {
    error.value = "La contraseña debe tener al menos 6 caracteres.";
    return;
  }

  const { data, error: signupError } = await supabase.auth.signUp({
    email: email.value,
    password: password.value,
    options: {
      data: {
        full_name: name.value,
      },
    },
  });

  if (signupError) {
    if (signupError.message.includes("User already registered")) {
      error.value = "Este correo ya está registrado. Intenta iniciar sesión.";
      mode.value = "login";
    } else {
      error.value = signupError.message;
    }
  } else {
    if (data.user && !data.user.email_confirmed_at) {
      success.value =
        "¡Cuenta creada! Revisa tu correo para confirmar tu cuenta.";
      // Don't redirect yet, wait for email confirmation
    } else {
      // User was created and is already confirmed
      await handleSuccessfulAuth();
    }
  }
}

async function handleSuccessfulAuth() {
  // Check if user was trying to save a trip
  if (action === "save-trip" || action === "book-trip") {
    // Check for pending trip data in session storage
    const pendingSimple = sessionStorage.getItem("pendingSimpleTrip");
    const pendingCustomized = sessionStorage.getItem("pendingCustomizedTrip");

    if (pendingSimple || pendingCustomized) {
      // Store a flag to trigger save attempt after redirect
      sessionStorage.setItem("attemptSaveAfterLogin", "true");
    }
  }

  // Redirect to the original page
  await router.push(redirectUrl);
}

// Watch for user authentication changes
const user = useSupabaseUser();
watch(user, (newUser) => {
  if (newUser && !loading.value) {
    // User is now authenticated, handle redirect
    handleSuccessfulAuth();
  }
});
</script>

<style scoped></style>
